import KuramaTable from '@precis-digital/kurama/Table';
import { styled } from '@precis-digital/kurama/utils';
import { type ColumnFiltersState, type ColumnOrderState, type ColumnPinningState, type ColumnSizingInfoState, type ColumnSizingState, getCoreRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, type RowModel, type RowSelectionState, type SortingState, type Updater } from '@tanstack/react-table';
import isEqual from 'lodash/isEqual';
import { type Dispatch, type ReactElement, type RefObject, type SetStateAction, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ChartCardStateAction, ChartChoicesMap, ElementsDescriptionsMap, TotalsAndRangeData } from '../../internalTypes';
import type { ChartCardTableCustomisationsProps, ChartCardTFunction, ChartDataRow, ChartSelections } from '../../types';
import TableColumnDnDProvider from './TableColumnDndProvider';
import TablePagination from './TablePagination';
import TableBody from './body/TableBody';
import { ROW_SELECTION_COLUMN_DEF } from './constants';
import TableFooter from './footer/TableFooter';
import TableHead from './head/TableHead';
import { useChartCardTable, useSvgToPngObjectUrl } from './hooks';
import { type ChartCardTable, type ChartCardTableColumnDef } from './tanstack';
import type { ColorScaleColumnMap, ColumnSetDisplayColorScaleFn, ColumnSetSortFn, CompleteTablePaginationState, TablePaginationState } from './types';
import { buildKey, clampColumnSizingToColumnDefs, getColumnId, getInitialColorScaleColumnMapFromChartCardTableColumns, getInitialColumnOrderFromChartCardTableColumns, getInitialPinningsFromChartCardTableColumns, getInitialSortingFromChartCardTableColumns } from './utils';
import { getColumnsInitialSizing, getFilledColumnSizing } from './utils/columnSizing';
import { fuzzyFilter } from './utils/filterAndSortingFns';
import { useChartCardDefaults } from '../../hooks/useChartCardDefaults';
interface TableProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> extends Required<Omit<ChartCardTableCustomisationsProps, 'tableSize' | 'useBodyScrollDecoration' | 'allowToggleUsePagination' | 'allowColumnsSelection' | 'useOverscrollContain' | 'onOpenColumnFilter'>> {
  tableRef: RefObject<ChartCardTable<DataKey, DataRow> | null>;
  resetTableRef: RefObject<(() => void) | null>;
  data: DataRow[];
  setCompletePaginationState: Dispatch<SetStateAction<CompleteTablePaginationState | null>>;
  globalFilter: string;
  setGlobalFilter: Dispatch<SetStateAction<string>>;
  totalsAndRangeData: TotalsAndRangeData<DataKey>;
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>;
  selections: ChartSelections<DataKey>;
  choicesList: DataKey[];
  choicesMap: ChartChoicesMap<DataKey, DataRow>;
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  containerClientWidth: number;
  tableHeadRootRef: RefObject<HTMLTableSectionElement | null>;
  tableFooterRootRef: RefObject<HTMLTableSectionElement | null>;
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
  isServerFetch: boolean;
  onOpenColumnFilter?: (columnId: string) => void;
  serverTotalRows?: number;
  serverPagination?: TablePaginationState;
  serverSorting?: SortingState;
  onServerPaginationChange?: (updater: TablePaginationState | ((prev: TablePaginationState) => TablePaginationState)) => void;
  onServerSortingChange?: (updater: SortingState | ((prev: SortingState) => SortingState)) => void;
  isFetchLoading?: boolean;
}
const defaultColumnSizingInfoState = (): ColumnSizingInfoState => ({
  columnSizingStart: [],
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false,
  startOffset: null,
  startSize: null
});
const Table = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  tableRef,
  resetTableRef,
  data,
  setCompletePaginationState,
  globalFilter,
  setGlobalFilter,
  totalsAndRangeData,
  columns,
  selections,
  choicesList,
  choicesMap,
  elementsDescriptionsMap,
  tableContainerRef,
  containerClientWidth,
  tableHeadRootRef,
  tableFooterRootRef,
  usePagination,
  useFuzzySearch,
  useDefaultSorting,
  useDefaultPinning,
  useColumnsDecorations,
  allowRowsSelection,
  allowMultipleColumnsSorting,
  allowColumnFiltering,
  onOpenColumnFilter,
  allowDateFiltering,
  allowColumnResizing,
  allowColumnAutoResizing,
  allowColumnPinning,
  allowColumnReordering,
  enableNullDimensionValue,
  isServerFetch,
  serverTotalRows,
  serverPagination,
  serverSorting,
  onServerPaginationChange,
  onServerSortingChange,
  isFetchLoading,
  dispatch,
  t
}: TableProps<DataKey, DataRow>): ReactElement => {
  const defaults = useChartCardDefaults();
  const initialState = useMemo(() => {
    const columnPinning = getInitialPinningsFromChartCardTableColumns<DataKey, DataRow>({
      useDefaultPinning,
      columns,
      elementsDescriptionsMap,
      allowColumnPinning,
      allowRowsSelection
    });
    return {
      rowSelection: {},
      columnFilters: [],
      sorting: getInitialSortingFromChartCardTableColumns<DataKey, DataRow>({
        useDefaultSorting,
        columns,
        elementsDescriptionsMap
      }),
      columnPinning,
      columnOrder: getInitialColumnOrderFromChartCardTableColumns<DataKey, DataRow>(columns, columnPinning),
      colorScaleColumnMap: getInitialColorScaleColumnMapFromChartCardTableColumns<DataKey, DataRow>(columns, elementsDescriptionsMap),
      columnSizing: getColumnsInitialSizing<DataKey, DataRow>(columns),
      pagination: {
        pageIndex: 0,
        pageSize: serverPagination?.pageSize ?? 10
      }
    };
  }, [useDefaultPinning, columns, elementsDescriptionsMap, allowColumnPinning, allowRowsSelection, useDefaultSorting, serverPagination?.pageSize]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(initialState.rowSelection);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialState.columnFilters);
  const [localSorting, setLocalSorting] = useState<SortingState>(initialState.sorting);
  const [columnPinning_0, setColumnPinning] = useState<ColumnPinningState>(initialState.columnPinning);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initialState.columnOrder);
  const [colorScaleColumnMap, setColorScaleColumnMap] = useState<ColorScaleColumnMap>(initialState.colorScaleColumnMap);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(initialState.columnSizing);
  const [localPagination, setLocalPagination] = useState<TablePaginationState>(initialState.pagination);
  const sorting = isServerFetch ? serverSorting ?? localSorting : localSorting;
  const setSorting = isServerFetch ? onServerSortingChange ?? setLocalSorting : setLocalSorting;
  const pagination = isServerFetch ? serverPagination ?? localPagination : localPagination;
  const setPagination = isServerFetch ? onServerPaginationChange ?? setLocalPagination : setLocalPagination;
  const stateIsInitial = useMemo(() => {
    return isEqual(rowSelection, initialState.rowSelection) && isEqual(columnFilters, initialState.columnFilters) && isEqual(sorting, initialState.sorting) && isEqual(columnPinning_0, initialState.columnPinning) && isEqual(columnOrder, initialState.columnOrder) && isEqual(colorScaleColumnMap, initialState.colorScaleColumnMap) && isEqual(columnSizing, initialState.columnSizing) && isEqual(pagination, initialState.pagination);
  }, [rowSelection, columnFilters, sorting, columnPinning_0, columnOrder, colorScaleColumnMap, columnSizing, pagination, initialState]);
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>(defaultColumnSizingInfoState());
  const resetTableHandler = useCallback(() => {
    setRowSelection(initialState.rowSelection);
    setColumnFilters(initialState.columnFilters);
    setSorting(initialState.sorting);
    setColumnPinning(initialState.columnPinning);
    setColumnOrder(initialState.columnOrder);
    setColorScaleColumnMap(initialState.colorScaleColumnMap);
    setColumnSizing(initialState.columnSizing);
    setPagination(initialState.pagination);
  }, [initialState, setSorting, setPagination]);
  resetTableRef.current = stateIsInitial ? null : resetTableHandler;
  const filledColumnSizingRef = useRef<ColumnSizingState>(initialState.columnSizing);
  const onColumnSizingChange = useCallback((updater: Updater<ColumnSizingState>) => {
    setColumnSizing(() => {
      // Apply the updater to the displayed (possibly filled) sizes so resize
      // deltas stay relative to what the user sees.
      const next = typeof updater === 'function' ? updater(filledColumnSizingRef.current) : updater;
      const instance = tableRef.current;
      if (instance == null) {
        return next;
      }
      return clampColumnSizingToColumnDefs<DataKey, DataRow>(instance, next);
    });
  }, [tableRef]);
  const selectedColumnsIds = selections['columns' as DataKey] as string[];
  const selectedColumns_0 = useMemo(() => {
    const selectedColumns = columns.filter(column => {
      const id = getColumnId(column);
      return selectedColumnsIds.includes(id);
    });
    if (allowRowsSelection) {
      selectedColumns.unshift(ROW_SELECTION_COLUMN_DEF as ChartCardTableColumnDef<DataKey, DataRow>);
    }
    return selectedColumns;
  }, [columns, selectedColumnsIds, allowRowsSelection]);
  const filledColumnSizing = useMemo(() => getFilledColumnSizing<DataKey, DataRow>(selectedColumns_0, columnSizing, containerClientWidth), [selectedColumns_0, columnSizing, containerClientWidth]);
  filledColumnSizingRef.current = filledColumnSizing;
  const [resizerSvg, setResizerSvg] = useState<SVGSVGElement | null>(null);
  useLayoutEffect(() => {
    setResizerSvg(document.querySelector<SVGSVGElement>('#icon-templates svg[data-resize-type="left-right"]'));
  }, []);
  const resizerCursorUrl = useSvgToPngObjectUrl(resizerSvg, {
    width: defaults.table.resizeIconSize,
    height: defaults.table.resizeIconSize,
    pixelRatio: 1
  });
  const pageCount = isServerFetch ? Math.max(1, Math.ceil((serverTotalRows ?? 0) / pagination.pageSize)) : Math.ceil(data.length / pagination.pageSize);
  const paginationRowModelFactory = useMemo<(table: ChartCardTable<DataKey, DataRow>) => () => RowModel<DataRow>>(() => usePagination && !isServerFetch ? getPaginationRowModel<DataRow>() as (table: ChartCardTable<DataKey, DataRow>) => () => RowModel<DataRow> : (tableInstance: ChartCardTable<DataKey, DataRow>) => () => tableInstance.getPrePaginationRowModel(), [usePagination, isServerFetch]);
  const table = useChartCardTable<DataKey, DataRow>({
    data,
    columns: selectedColumns_0,
    columnResizeMode: 'onChange',
    enableColumnResizing: allowColumnResizing,
    defaultColumn: defaults.table.defaultColumn,
    getCoreRowModel: getCoreRowModel<DataRow>(),
    ...(isServerFetch ? {
      manualPagination: true,
      manualSorting: true,
      pageCount,
      autoResetPageIndex: false
    } : {
      getSortedRowModel: getSortedRowModel<DataRow>()
    }),
    ...((allowColumnFiltering || useFuzzySearch) && !isServerFetch && {
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedMinMaxValues: getFacetedMinMaxValues(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      getFilteredRowModel: getFilteredRowModel(),
      onColumnFiltersChange: setColumnFilters
    }),
    ...(useFuzzySearch && !isServerFetch && {
      filterFns: {
        fuzzy: fuzzyFilter
      },
      globalFilterFn: fuzzyFilter,
      onGlobalFilterChange: setGlobalFilter
    }),
    onSortingChange: setSorting,
    onColumnPinningChange: setColumnPinning,
    ...(allowColumnReordering && {
      onColumnOrderChange: setColumnOrder
    }),
    ...(allowRowsSelection && {
      onRowSelectionChange: setRowSelection
    }),
    ...(allowColumnResizing && {
      onColumnSizingChange,
      onColumnSizingInfoChange: setColumnSizingInfo
    }),
    getPaginationRowModel: paginationRowModelFactory,
    ...(usePagination && {
      onPaginationChange: setPagination
    }),
    state: {
      sorting,
      columnSizing: filledColumnSizing,
      columnSizingInfo,
      columnPinning: columnPinning_0,
      ...(allowColumnReordering && {
        columnOrder
      }),
      ...(allowRowsSelection && {
        rowSelection
      }),
      ...(usePagination && {
        pagination
      }),
      ...(allowColumnFiltering && !isServerFetch && {
        columnFilters
      }),
      ...(useFuzzySearch && !isServerFetch && {
        globalFilter
      })
    }
  });
  tableRef.current = table;
  const paginationRowModelFactoryRef = useRef(paginationRowModelFactory);
  if (paginationRowModelFactoryRef.current !== paginationRowModelFactory) {
    Reflect.deleteProperty(table, '_getPaginationRowModel');
    paginationRowModelFactoryRef.current = paginationRowModelFactory;
  }
  const totalRowsForPagination = isServerFetch ? serverTotalRows ?? 0 : data.length;
  const filteredRowsCount = isServerFetch ? serverTotalRows ?? 0 : table.getFilteredRowModel().rows.length;
  useEffect(() => {
    setCompletePaginationState(prev => ({
      ...prev,
      totalRowsCount: totalRowsForPagination,
      rowsCount: filteredRowsCount,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      pageCount,
      canPreviousPage: pagination.pageIndex > 0,
      canNextPage: pagination.pageIndex < pageCount - 1
    }));
  }, [pagination, setCompletePaginationState, totalRowsForPagination, filteredRowsCount, pageCount]);
  const tableState = table.getState();
  const columnSizeVars = useMemo<Record<string, number>>(() => {
    const headers = table.getFlatHeaders();
    const colSizes: Record<string, number> = {};
    for (const header of headers) {
      colSizes[`--header-${header.id}-size`] = tableState.columnSizing[header.id] ?? header.getSize();
      colSizes[`--col-${header.column.id}-size`] = tableState.columnSizing[header.column.id] ?? header.column.getSize();
    }
    const leftVisible = table.getLeftVisibleLeafColumns().filter(column_0 => selectedColumnsIds.includes(column_0.id) || column_0.id === ROW_SELECTION_COLUMN_DEF.id);
    let leftOffset = 0;
    for (const column_1 of leftVisible) {
      if (tableState.columnPinning.left?.includes(column_1.id) === true) {
        colSizes[`--pinned-left-${column_1.id}`] = leftOffset;
        leftOffset += column_1.getSize();
      }
    }
    const rightVisible = table.getRightVisibleLeafColumns().filter(column_2 => selectedColumnsIds.includes(column_2.id) || column_2.id === ROW_SELECTION_COLUMN_DEF.id).toReversed();
    let rightOffset = 0;
    for (const column_3 of rightVisible) {
      if (tableState.columnPinning.right?.includes(column_3.id) === true) {
        colSizes[`--pinned-right-${column_3.id}`] = rightOffset;
        rightOffset += column_3.getSize();
      }
    }
    return colSizes;
  }, [table, tableState.columnSizing, tableState.columnPinning, selectedColumnsIds]);
  const setSort: ColumnSetSortFn = useCallback((columnId, sort) => {
    setSorting(prev_0 => {
      const newSorting = allowMultipleColumnsSorting ? prev_0.filter(columnSort => columnSort.id !== columnId) : [];
      if (sort === false) {
        return newSorting;
      }
      return [{
        id: columnId,
        desc: sort === 'desc'
      }, ...newSorting];
    });
  }, [setSorting, allowMultipleColumnsSorting]);
  const setDisplayColorScale: ColumnSetDisplayColorScaleFn = useCallback((columnId_0, displayColorScale) => {
    setColorScaleColumnMap(prev_1 => {
      const colorScaleState = prev_1[columnId_0];
      if (colorScaleState == null) {
        return prev_1;
      }
      return {
        ...prev_1,
        [columnId_0]: {
          ...colorScaleState,
          display: displayColorScale
        }
      };
    });
  }, [setColorScaleColumnMap]);
  const tableSections = [<TableHead<DataKey, DataRow> key={buildKey('tableHead', selectedColumnsIds, columnPinning_0)} table={table} elementsDescriptionsMap={elementsDescriptionsMap} rowSelection={rowSelection} sorting={sorting} setSort={setSort} selectedColumnsIds={selectedColumnsIds} columnPinning={columnPinning_0} columnFilters={columnFilters} isResizingColumn={columnSizingInfo.isResizingColumn} resizerCursorUrl={resizerCursorUrl} colorScaleColumnMap={colorScaleColumnMap} setDisplayColorScale={setDisplayColorScale} useColumnsDecorations={useColumnsDecorations} allowColumnFiltering={allowColumnFiltering} allowDateFiltering={allowDateFiltering} onOpenColumnFilter={onOpenColumnFilter} allowColumnResizing={allowColumnResizing} allowColumnAutoResizing={allowColumnAutoResizing} allowColumnPinning={allowColumnPinning} allowColumnReordering={allowColumnReordering} headerRootRef={tableHeadRootRef} isFetchLoading={isFetchLoading} dispatch={dispatch} t={t} />, <TableFooter<DataKey, DataRow> ref={tableFooterRootRef} key={buildKey('tableFooter', selectedColumnsIds, columnPinning_0)} elementsDescriptionsMap={elementsDescriptionsMap} table={table} columnPinning={columnPinning_0} allowColumnResizing={allowColumnResizing} totalsData={totalsAndRangeData.totals} preferTotalsData={isServerFetch} t={t}>
      {usePagination && <TablePagination tableRef={tableRef} completePaginationState={{
      totalRowsCount: totalRowsForPagination,
      rowsCount: filteredRowsCount,
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
      pageCount,
      canPreviousPage: pagination.pageIndex > 0,
      canNextPage: pagination.pageIndex < pageCount - 1
    }} />}
    </TableFooter>, <TableBody<DataKey, DataRow> key={buildKey(usePagination ? 'paginatedBody' : 'unpaginatedBody', selectedColumnsIds, columnPinning_0)} elementsDescriptionsMap={elementsDescriptionsMap} table={table} rowSelection={rowSelection} columnPinning={columnPinning_0} totalsAndRangeData={totalsAndRangeData} colorScaleColumnMap={colorScaleColumnMap} allowColumnResizing={allowColumnResizing} enableNullDimensionValue={enableNullDimensionValue} tableContainerRef={tableContainerRef} isFetchLoading={isFetchLoading} t={t} />];
  return <StyledTable columnSizeVars={columnSizeVars} width={table.getTotalSize()}>
      {allowColumnReordering ? <TableColumnDnDProvider<DataKey, DataRow> tableRef={tableRef} tableContainerRef={tableContainerRef} tableHeadRootRef={tableHeadRootRef} allowColumnPinning={allowColumnPinning} columnPinning={columnPinning_0}>
          {tableSections}
        </TableColumnDnDProvider> : tableSections}
    </StyledTable>;
};
const StyledTable = styled(KuramaTable, {
  shouldForwardProp: prop => prop !== 'columnSizeVars' && prop !== 'width'
})(({
  columnSizeVars,
  width
}: {
  columnSizeVars: Record<string, number>;
  width: number;
}) => ({
  display: 'grid',
  // Fill the scroll container so a short last page does not leave a gap below the footer.
  // Rows follow CSS order (head → body → footer): body absorbs spare height.
  minHeight: '100%',
  gridTemplateRows: 'auto 1fr auto',
  ...columnSizeVars,
  width
}));
export default Table;